import Cookies from 'js-cookie';
import { publishSticky, subscribe } from '@wh/message-bus';
import { AccountInfoStateType } from '@central-products/interface-account-info';
import { ObSessionStateType } from '@central-products/interface-obsession-service';
import { isNative } from '../../utils';

const GOT_LOGIN_STATE_EVENT = 'gotLoginState';
const REQUEST_GOT_LOGIN_STATE_EVENT = 'requestGotLoginState';

const CURRENCY_TO_OB_CURRENCY_MAP = {
  GBP: '&pound;',
  EUR: '&euro;',
  AUD: '$',
  CAD: 'C $',
  USD: '$',
  DKK: 'DKK&nbsp;',
  JPY: '&yen;',
  SGD: '$',
  HKD: '$',
  CHF: 'CHF&nbsp;',
};

export const isCOMJurisdiction = () => {
  const hostMatch = /.+\.williamhill(?:-.+)?\.(.+)/.exec(window.location.hostname || window.location.host);

  if (!hostMatch) {
    return false;
  }

  return hostMatch[1] === 'com';
};

export const removeOBCookies = () => {
  const hostMatch = /.+(\.williamhill(?:-.+)?\..+)/.exec(window.location.hostname || window.location.host);

  if (!hostMatch) {
    return;
  }

  ['cas_login', 'cas_ssl_login', 'cust_login', 'cust_ssl_login'].forEach((cookie) => {
    Cookies.remove(cookie, {
      domain: hostMatch[1],
      path: '/',
    });
  });
};

export const createGotLoginStatePayload = (hasValidSession = false, accountInfo = {}) => {
  const {
    obAvFlags = '',
    firstName: obClientName = '',
    accountId: obAccountNumber = '',
    currency: obBalanceCurrency = '',
    currencyCode,
    balance: obBalanceAmount = '',
    obMessageNumber = '',
    obLastLogin = '',
    obFormAction = '',
    obFormHidden = '',
    obUsernameName = '',
    obPasswordName = '',
  } = accountInfo;

  return {
    isLoggedIn: hasValidSession,
    cust: {
      auth: Cookies.get('cust_auth'),
      login: Cookies.get('cust_login'),
      sslLogin: Cookies.get('cust_ssl_login'),
    },
    data: {
      obAvFlags,
      obClientName,
      obAccountNumber,
      obBalanceCurrency: Object.prototype.hasOwnProperty.call(CURRENCY_TO_OB_CURRENCY_MAP, currencyCode)
        ? CURRENCY_TO_OB_CURRENCY_MAP[currencyCode]
        : obBalanceCurrency,
      obBalanceAmount,
      obMessageNumber,
      obLastLogin,
      obFormAction,
      obFormHidden,
      obUsernameName,
      obPasswordName,
    },
  };
};
export const getAccountInfo = (accountInfoFeature, abortController) =>
  // eslint-disable-next-line no-async-promise-executor
  new Promise(async (resolve, reject) => {
    try {
      if (abortController.signal.aborted) {
        reject();
        return;
      }

      const currAccountInfoState = await accountInfoFeature.getState();

      if (currAccountInfoState.state === AccountInfoStateType.ACCOUNT_INFO_EXISTS) {
        resolve(currAccountInfoState);
        return;
      }

      let accountInfoStateSubscription = accountInfoFeature.onStateChanged((nextAccountInfoState) => {
        if (nextAccountInfoState.state !== AccountInfoStateType.ACCOUNT_INFO_EXISTS) {
          return;
        }

        if (accountInfoStateSubscription) {
          accountInfoStateSubscription();
          accountInfoStateSubscription = undefined;
        }

        resolve(nextAccountInfoState);
      });
      const onAbortCallback = () => {
        abortController.signal.removeEventListener('abort', onAbortCallback);
        if (accountInfoStateSubscription) {
          accountInfoStateSubscription();
          accountInfoStateSubscription = undefined;
        }
        reject();
      };
      abortController.signal.addEventListener('abort', onAbortCallback);
    } catch (error) {}
  });

let abortController;

export const handleOBSessionStateChanged =
  (accountInfoFeature) =>
  async ({ state }) => {
    if (abortController) {
      abortController.abort();
      abortController = undefined;
    }

    if (state === ObSessionStateType.SESSION_EXISTS) {
      try {
        abortController = new AbortController();
        publishSticky(
          GOT_LOGIN_STATE_EVENT,
          createGotLoginStatePayload(true, await getAccountInfo(accountInfoFeature, abortController))
        );
      } catch (error) {}
    }

    if (state === ObSessionStateType.NO_SESSION) {
      publishSticky(GOT_LOGIN_STATE_EVENT, createGotLoginStatePayload(false));
    }

    if (isCOMJurisdiction() && isNative()) {
      return;
    }

    if (state === ObSessionStateType.NO_SESSION) {
      removeOBCookies();
    }
  };

export const handleRequestGotLoginState = (obSessionFeature, accountInfoFeature) => async () => {
  try {
    const [obSessionState, accountInfoState] = await Promise.all([
      obSessionFeature.getState(),
      accountInfoFeature.getState(),
    ]);

    publishSticky(
      GOT_LOGIN_STATE_EVENT,
      obSessionState.state === ObSessionStateType.SESSION_EXISTS
        ? createGotLoginStatePayload(true, accountInfoState)
        : createGotLoginStatePayload(false)
    );
  } catch (error) {}
};

export const sportsbookSessionPlugin = {
  async postBootstrap({ bootstrapperFeatures }) {
    const boundHandleOBSessionState = handleOBSessionStateChanged(bootstrapperFeatures['account-info']);
    const boundHandleRequestGotLoginState = handleRequestGotLoginState(
      bootstrapperFeatures['obsession-service'],
      bootstrapperFeatures['account-info']
    );
    const obSessionState = await bootstrapperFeatures['obsession-service'].getState();

    bootstrapperFeatures['obsession-service'].onStateChanged(boundHandleOBSessionState);
    boundHandleOBSessionState(obSessionState);

    subscribe({
      topic: REQUEST_GOT_LOGIN_STATE_EVENT,
      callback: boundHandleRequestGotLoginState,
    });
  },
};
